import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, Snackbar, Alert } from '@mui/material';
import styled from 'styled-components';
import DashboardNavbar from '../dashboardNavbar';
import { SYSTEM_TABS } from '../admin/adminSectionTabs';
import DataTable from '../ui/DataTable';
import { Pencil, Power, Trash2 } from 'lucide-react';
import { Btn, Tag, StatusBadge, RowBetween, Muted, Seg } from '../ui/primitives';
import GroupedRowActionSheet from '../ui/GroupedRowActionSheet';
import { useConfirm } from '../ui/ConfirmProvider';
import {
  fetchActivationCodes,
  setActivationCodeActive,
  deleteActivationCode,
  fetchOrganizations,
} from '../services/activationCodeAdminApi';
import ActivationCodeFormDialog from './ActivationCodeFormDialog';
import ActivationCodeDetailsDialog from './ActivationCodeDetailsDialog';

const PAGE_SIZE = 12;

const TIER_TONE = {
  starter: 'gray',
  professional: 'blue',
  growth: 'accent',
  organization: 'green',
  enterprise: 'amber',
};

/** epoch seconds → short date, or an em dash when unset. */
function fmtDate(epoch) {
  if (epoch === null || epoch === undefined || epoch === '') return '—';
  const d = new Date(Number(epoch) * 1000);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Super-admin console for creating activation codes and enabling/disabling them. */
export default function ActivationCodeManager() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [scopeFilter, setScopeFilter] = useState('all'); // 'all' | 'global' | 'org'
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [orgNameById, setOrgNameById] = useState({});
  const askConfirmation = useConfirm();

  // Resolve org ids → names once, so org-scoped codes show the org name.
  useEffect(() => {
    let alive = true;
    fetchOrganizations()
      .then((data) => {
        if (!alive) return;
        const map = {};
        (Array.isArray(data) ? data : []).forEach((o) => { map[o.orgId] = o.orgName; });
        setOrgNameById(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const showToast = (msg, severity = 'success') => setToast({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchActivationCodes();
      setCodes(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load activation codes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (c) => { setEditing(c); setFormOpen(true); };

  const handleToggleActive = async (c) => {
    try {
      await setActivationCodeActive(c.activationCodeId, !c.active);
      showToast(`Code ${c.active ? 'deactivated' : 'activated'}.`);
      load();
    } catch (e) {
      showToast(e.message || 'Update failed.', 'error');
    }
  };

  const handleDelete = async (c) => {
    const ok = await askConfirmation({
      title: 'Delete activation code?',
      message: `“${c.code}” will be permanently removed. Plans already redeemed with it stay active — only the code is deleted. This can’t be undone.`,
      confirmLabel: 'Delete code',
      variant: 'danger',
      requireTypedConfirm: false,
    });
    if (!ok) return;
    try {
      await deleteActivationCode(c.activationCodeId);
      showToast('Activation code deleted.');
      load();
    } catch (e) {
      showToast(e.message || 'Delete failed.', 'error');
    }
  };

  const handleRowAction = (key, c) => {
    if (key === 'edit') openEdit(c);
    else if (key === 'toggle') handleToggleActive(c);
    else if (key === 'delete') handleDelete(c);
  };

  const handleSaved = () => {
    setFormOpen(false);
    setEditing(null);
    showToast('Activation code saved.');
    load();
  };

  const activeCount = codes.filter((c) => c.active).length;
  const globalCount = codes.filter((c) => !c.orgId).length;
  const orgCount = codes.length - globalCount;
  const filtered = codes.filter((c) => {
    if (scopeFilter === 'global') return !c.orgId;
    if (scopeFilter === 'org') return !!c.orgId;
    return true;
  });

  return (
    <div>
      <DashboardNavbar menuItems={SYSTEM_TABS} />
      <Shell>
        <Inner>
          <PageIntro>
            <RowBetween>
              <div>
                <h2>Activation codes</h2>
                <p>
                  Issue codes an instructor can redeem to unlock a plan without paying by card —
                  useful for enterprise deals, schools, and events. Set the plan, validity window,
                  and how many times each code can be used.
                </p>
              </div>
              <Btn onClick={openCreate}>+ New code</Btn>
            </RowBetween>
            {!loading && !error && (
              <StatsRow>
                <span><b>{codes.length}</b> codes</span>
                <span><b>{activeCount}</b> active</span>
              </StatsRow>
            )}
          </PageIntro>

          {loading && (
            <LoadingWrap><CircularProgress size={28} /></LoadingWrap>
          )}

          {!loading && error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>
          )}

          {!loading && !error && (
            <>
            <ScopeTabs>
              <Seg>
                <button className={scopeFilter === 'all' ? 'is-on' : ''} onClick={() => setScopeFilter('all')}>All · {codes.length}</button>
                <button className={scopeFilter === 'global' ? 'is-on' : ''} onClick={() => setScopeFilter('global')}>Global · {globalCount}</button>
                <button className={scopeFilter === 'org' ? 'is-on' : ''} onClick={() => setScopeFilter('org')}>Organization · {orgCount}</button>
              </Seg>
            </ScopeTabs>
            <DataTable
              rows={filtered}
              rowKey={(r) => r.activationCodeId}
              pageSize={PAGE_SIZE}
              onRowClick={(r) => { setViewing(r); setDetailsOpen(true); }}
              searchKeys={['code', 'planId', 'activationCodeId', 'orgId']}
              primary={{
                header: 'Code',
                label: (r) => r.code || '(none)',
                sub: (r) => (r.orgId ? `Org: ${r.orgName || orgNameById[r.orgId] || r.orgId}` : 'Any organization'),
                short: (r) => (r.code || '?').charAt(0).toUpperCase(),
                sortKey: 'code',
              }}
              columns={[
                {
                  header: 'Scope',
                  render: (r) => (r.orgId ? <Tag $tone="accent">Org · one-time</Tag> : <Muted>Global</Muted>),
                },
                {
                  header: 'Plan',
                  render: (r) => (
                    <Tag $tone={TIER_TONE[r.planId] || 'gray'}>{cap(r.planId) || '—'}</Tag>
                  ),
                },
                {
                  header: 'Billing',
                  render: (r) => <Muted>{r.applicability === 'both' ? 'Monthly / yearly' : cap(r.applicability)}</Muted>,
                },
                {
                  header: 'Validity',
                  render: (r) => (
                    <Muted style={{ whiteSpace: 'nowrap' }}>
                      {fmtDate(r.validFrom)} – {fmtDate(r.validUntil)}
                    </Muted>
                  ),
                },
                {
                  header: 'Uses',
                  width: 90,
                  render: (r) => (
                    <span className="tnum">
                      {(r.redemptionCount ?? 0)}
                      {r.maxTotalUses ? ` / ${r.maxTotalUses}` : ' / ∞'}
                    </span>
                  ),
                },
                {
                  header: 'Status',
                  width: 110,
                  render: (r) => (
                    <StatusBadge
                      $active={r.active}
                      onClick={(e) => { e.stopPropagation(); handleToggleActive(r); }}
                      style={{ cursor: 'pointer' }}
                      title="Click to toggle"
                    >
                      {r.active ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  ),
                },
              ]}
              actions={(r) => (
                <GroupedRowActionSheet
                  entityLabel="Activation code"
                  title={r.code}
                  layoutKey={String(r.active)}
                  primary={{ key: 'edit', label: 'Edit', icon: <Pencil size={16} /> }}
                  items={[{
                    key: 'toggle',
                    label: r.active ? 'Mark inactive' : 'Mark active',
                    icon: <Power size={16} />,
                  }]}
                  dangerItem={{ key: 'delete', label: 'Delete code', icon: <Trash2 size={16} /> }}
                  onAction={(key) => handleRowAction(key, r)}
                />
              )}
              actionsColumnWidth={64}
              emptyText="No activation codes yet. Create your first one."
            />
            </>
          )}
        </Inner>
      </Shell>

      <ActivationCodeDetailsDialog
        open={detailsOpen}
        code={viewing}
        orgName={viewing ? orgNameById[viewing.orgId] : ''}
        onClose={() => { setDetailsOpen(false); setViewing(null); }}
        onEdit={() => { setDetailsOpen(false); openEdit(viewing); }}
      />

      <ActivationCodeFormDialog
        open={formOpen}
        code={editing}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={handleSaved}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
}

const Shell = styled.div`
  padding: 24px 28px 60px;
`;

const Inner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const PageIntro = styled.div`
  margin-bottom: 20px;
  h2 { font-family: ${({ theme }) => theme.fontHead}; font-size: 22px; font-weight: 760; margin: 0 0 4px; letter-spacing: -.01em; }
  p { margin: 0; color: ${({ theme }) => theme.textMuted}; font-size: 13.5px; max-width: 640px; }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.textMuted};
  b { color: ${({ theme }) => theme.text}; }
`;

const LoadingWrap = styled.div`
  display: grid;
  place-items: center;
  padding: 48px 0;
`;

const ScopeTabs = styled.div`
  margin-bottom: 14px;
`;
